import { useState, type ReactNode } from "react";
import { Layout } from "@/components/layout/Layout";
import { useAuth } from "@/lib/authContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Mail, Inbox, Plus, Trash2, Power, Pencil, X, Check,
  HardDrive, CheckCircle2, AlertCircle, Loader2,
  ChevronDown, ChevronRight, UserPlus, Users2, CalendarDays,
  Upload, Tags, ExternalLink, MessageSquare, Send, Zap, FileSignature,
} from "lucide-react";
import {
  fetchWithAuth, emailApi, EmailAccount, api,
  getSmartVaultStatus, testSmartVaultConnection, type SVStatus, type SVConfigured,
} from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useLocation, Link } from "wouter";

// ─── Collapsible section ─────────────────────────────────────────────────────

function Section({
  icon,
  title,
  description,
  children,
  defaultOpen = false,
}: {
  icon: ReactNode;
  title: string;
  description?: string;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card>
      <CardHeader className="cursor-pointer select-none py-4" onClick={() => setOpen(v => !v)}>
        <CardTitle className="flex items-center gap-2">
          {icon}
          <span className="flex-1">{title}</span>
          {open ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
        </CardTitle>
        {open && description && <CardDescription className="mt-1.5">{description}</CardDescription>}
      </CardHeader>
      {open && <CardContent>{children}</CardContent>}
    </Card>
  );
}

// ─── Link card (for pages that are too complex to inline) ────────────────────

function LinkSection({
  icon,
  title,
  description,
  href,
  buttonLabel = "Manage",
}: {
  icon: ReactNode;
  title: string;
  description: string;
  href: string;
  buttonLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <CardHeader className="cursor-pointer select-none py-4" onClick={() => setOpen(v => !v)}>
        <CardTitle className="flex items-center gap-2">
          {icon}
          <span className="flex-1">{title}</span>
          {open ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
        </CardTitle>
        {open && <CardDescription className="mt-1.5">{description}</CardDescription>}
      </CardHeader>
      {open && (
        <CardContent>
          <Link href={href}>
            <Button variant="outline" size="sm" className="gap-2">
              <ExternalLink className="w-3.5 h-3.5" />
              {buttonLabel}
            </Button>
          </Link>
        </CardContent>
      )}
    </Card>
  );
}

// ─── Invite Team ─────────────────────────────────────────────────────────────

function InviteTeamSection() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", role: "staff" as "staff" | "contractor" | "manager" | "admin" });

  const inviteMutation = useMutation({
    mutationFn: () => api.inviteTeamMember(form),
    onSuccess: () => {
      toast({ title: "Invite sent", description: `Invite sent to ${form.email}.` });
      setForm({ name: "", email: "", role: "staff" });
    },
    onError: (err: Error) => toast({ title: "Invite failed", description: err.message, variant: "destructive" }),
  });

  return (
    <Section icon={<UserPlus className="w-5 h-5 text-primary" />} title="Invite Team Member" description="Send a setup email to a new staff member. They'll set their password on first login.">
      <form onSubmit={e => { e.preventDefault(); inviteMutation.mutate(); }} className="space-y-4 max-w-md">
        <div className="space-y-1.5">
          <Label>Full name *</Label>
          <Input placeholder="Jane Smith" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
        </div>
        <div className="space-y-1.5">
          <Label>Email address *</Label>
          <Input type="email" placeholder="jane@carpentercpas.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
        </div>
        <div className="space-y-1.5">
          <Label>Role</Label>
          <Select value={form.role} onValueChange={v => setForm({ ...form, role: v as any })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="staff">Staff — time entry, own projects</SelectItem>
              <SelectItem value="contractor">Contractor — same as staff, no financials or reports</SelectItem>
              <SelectItem value="manager">Manager — approve timesheets, all reports</SelectItem>
              {user?.role === "admin" && <SelectItem value="admin">Admin — full access</SelectItem>}
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" disabled={inviteMutation.isPending} className="gap-2">
          <Mail className="w-4 h-4" />
          {inviteMutation.isPending ? "Sending…" : "Send Invite"}
        </Button>
      </form>
    </Section>
  );
}

// ─── Activity Codes ───────────────────────────────────────────────────────────

type ActivityCode = {
  code: string;
  name: string;
  billable_default: boolean;
  is_active: boolean;
  category: string;
};

const CATEGORIES = ["work", "admin", "pto", "holiday"] as const; // must match activity_codes_category_check

function ActivityCodesSection() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [addCode, setAddCode] = useState("");
  const [addName, setAddName] = useState("");
  const [addCat, setAddCat] = useState("work");
  const [addBillable, setAddBillable] = useState(true);
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editCat, setEditCat] = useState("");
  const [editBillable, setEditBillable] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  const { data: codes = [], isLoading } = useQuery<ActivityCode[]>({
    queryKey: ["admin-activity-codes"],
    queryFn: () => fetchWithAuth<ActivityCode[]>("/api/admin/activity-codes"),
    staleTime: 30_000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["admin-activity-codes"] });

  const addMutation = useMutation({
    mutationFn: () => fetchWithAuth<ActivityCode>("/api/admin/activity-codes", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code: addCode, name: addName, category: addCat, billable_default: addBillable }),
    }),
    onSuccess: () => { setAddCode(""); setAddName(""); setAddCat("work"); setAddBillable(true); invalidate(); toast({ title: "Code added" }); },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ code, ...body }: { code: string; name: string; category: string; billable_default: boolean }) =>
      fetchWithAuth(`/api/admin/activity-codes/${code}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => { setEditingCode(null); invalidate(); toast({ title: "Code updated" }); },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const toggleMutation = useMutation({
    mutationFn: (code: string) => fetchWithAuth(`/api/admin/activity-codes/${code}/toggle`, { method: "PATCH" }),
    onSuccess: () => invalidate(),
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  function startEdit(c: ActivityCode) {
    setEditingCode(c.code);
    setEditName(c.name);
    setEditCat(c.category);
    setEditBillable(c.billable_default);
  }

  const visible = showInactive ? codes : codes.filter(c => c.is_active);

  return (
    <Section icon={<Tags className="w-5 h-5 text-primary" />} title="Activity Codes" description="Time entry codes used across the firm. Inactive codes are hidden from time entry but preserved on historical entries.">
      {/* Add form */}
      <div className="rounded-md border p-4 space-y-3 mb-4">
        <p className="text-sm font-medium">Add Code</p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs mb-1 block">Code *</Label>
            <Input value={addCode} onChange={e => setAddCode(e.target.value.toUpperCase())} placeholder="e.g. TAX" className="h-8 text-sm font-mono" maxLength={20} />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Name *</Label>
            <Input value={addName} onChange={e => setAddName(e.target.value)} placeholder="e.g. Tax Preparation" className="h-8 text-sm" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <Label className="text-xs mb-1 block">Category</Label>
            <Select value={addCat} onValueChange={setAddCat}>
              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 mt-4">
            <Switch checked={addBillable} onCheckedChange={setAddBillable} />
            <Label className="text-xs">Billable default</Label>
          </div>
          <Button size="sm" className="h-8 gap-1 mt-4" disabled={!addCode.trim() || !addName.trim() || addMutation.isPending} onClick={() => addMutation.mutate()}>
            <Plus className="w-3.5 h-3.5" /> Add
          </Button>
        </div>
      </div>

      {/* Toggle inactive visibility */}
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-muted-foreground">{codes.filter(c => c.is_active).length} active, {codes.filter(c => !c.is_active).length} inactive</p>
        <button className="text-xs text-muted-foreground hover:text-foreground underline" onClick={() => setShowInactive(v => !v)}>
          {showInactive ? "Hide inactive" : "Show inactive"}
        </button>
      </div>

      {/* Code list */}
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="border rounded-md divide-y overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-[80px_1fr_100px_90px_80px] gap-2 px-3 py-2 bg-muted/50 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            <span>Code</span>
            <span>Name</span>
            <span>Category</span>
            <span>Billable?</span>
            <span></span>
          </div>
          {visible.length === 0 && (
            <div className="px-3 py-4 text-sm text-muted-foreground">No codes.</div>
          )}
          {visible.map(c => (
            <div key={c.code} className={`${!c.is_active ? "bg-muted/30 opacity-60" : ""}`}>
              {editingCode === c.code ? (
                <div className="px-3 py-2 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs mb-1 block">Name</Label>
                      <Input value={editName} onChange={e => setEditName(e.target.value)} className="h-7 text-sm" />
                    </div>
                    <div>
                      <Label className="text-xs mb-1 block">Category</Label>
                      <Select value={editCat} onValueChange={setEditCat}>
                        <SelectTrigger className="h-7 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Switch checked={editBillable} onCheckedChange={setEditBillable} />
                      <Label className="text-xs">Billable default</Label>
                    </div>
                    <div className="flex gap-2 ml-auto">
                      <Button size="sm" className="h-7 gap-1" disabled={!editName.trim() || updateMutation.isPending}
                        onClick={() => updateMutation.mutate({ code: c.code, name: editName, category: editCat, billable_default: editBillable })}>
                        <Check className="w-3 h-3" /> Save
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 gap-1" onClick={() => setEditingCode(null)}>
                        <X className="w-3 h-3" /> Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-[80px_1fr_100px_90px_80px] gap-2 px-3 py-2 items-center text-sm">
                  <span className="font-mono text-xs font-semibold">{c.code}</span>
                  <span className="truncate">{c.name}</span>
                  <span>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 capitalize">{c.category}</Badge>
                  </span>
                  <span className="text-xs text-muted-foreground">{c.billable_default ? "Yes" : "No"}</span>
                  <div className="flex items-center gap-1 justify-end">
                    <Button variant="ghost" size="icon" className="h-6 w-6" title="Edit" onClick={() => startEdit(c)}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6" title={c.is_active ? "Deactivate" : "Activate"}
                      onClick={() => toggleMutation.mutate(c.code)} disabled={toggleMutation.isPending}>
                      <Power className={`w-3 h-3 ${c.is_active ? "text-green-600" : "text-muted-foreground"}`} />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

// ─── Email Config ─────────────────────────────────────────────────────────────

function EmailConfigSection() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const send = async () => {
    setStatus("sending"); setMessage("");
    try {
      const r = await fetchWithAuth<{ sent: boolean; to: string; message?: string }>("/api/admin/test-email", { method: "POST" });
      if (r.sent) { setStatus("success"); setMessage(`Sent to ${r.to}`); }
      else { setStatus("error"); setMessage(r.message || "Unknown error"); }
    } catch (e: any) { setStatus("error"); setMessage(e.message || "Request failed"); }
  };

  return (
    <Section icon={<Mail className="w-5 h-5 text-primary" />} title="Email Configuration" description="Test that Resend is configured correctly and emails are delivering.">
      <div className="flex items-center gap-4">
        <Button onClick={send} disabled={status === "sending"} variant="outline">
          {status === "sending" ? "Sending…" : "Send Test Email"}
        </Button>
        {status === "success" && <p className="text-sm text-green-600">Delivered — {message}</p>}
        {status === "error" && <p className="text-sm text-destructive">{message}</p>}
      </div>
    </Section>
  );
}

// ─── Daily Time Report ────────────────────────────────────────────────────────

function DailyTimeReportSection() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [errMsg, setErrMsg] = useState("");
  const [date, setDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split("T")[0];
  });

  const send = async () => {
    setStatus("sending");
    setErrMsg("");
    try {
      await fetchWithAuth("/api/admin/time-report/send-now", { method: "POST", body: JSON.stringify({ date }) });
      setStatus("success");
    } catch (e: any) {
      setStatus("error");
      setErrMsg(e.message || "Unknown error");
    }
  };

  return (
    <Section icon={<Mail className="w-5 h-5 text-primary" />} title="Daily Time Report" description="Sends automatically at 7:00 AM Eastern, Mon–Fri. Monday covers Friday.">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Report date</span>
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          />
        </div>
        <Button onClick={send} disabled={status === "sending"} variant="outline" className="mt-4">
          {status === "sending" ? "Sending…" : "Send Now"}
        </Button>
        {status === "success" && <p className="text-sm text-green-600 mt-4">Report sent to all admins & managers.</p>}
        {status === "error" && <p className="text-sm text-destructive mt-4">Failed: {errMsg}</p>}
      </div>
    </Section>
  );
}

// ─── Microsoft 365 ────────────────────────────────────────────────────────────

function M365Section() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newMailbox, setNewMailbox] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");
  const [isShared, setIsShared] = useState(false);
  const [isTammy, setIsTammy] = useState(false);
  const [newLinkedAccountId, setNewLinkedAccountId] = useState("");
  const [newOwnerId, setNewOwnerId] = useState(""); // "My Mail" owner (Phase 2) — only for a non-shared mailbox
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editMailbox, setEditMailbox] = useState("");
  const [editDisplayName, setEditDisplayName] = useState("");
  const [editIsShared, setEditIsShared] = useState(false);
  const [editIsTammy, setEditIsTammy] = useState(false);
  const [editLinkedAccountId, setEditLinkedAccountId] = useState("");
  const [editOwnerId, setEditOwnerId] = useState("");

  const { data: emailAccounts = [], isLoading } = useQuery<EmailAccount[]>({
    queryKey: ["email-accounts"],
    queryFn: () => emailApi.listEmailAccounts(),
  });
  const { data: allAccounts = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["accounts-list-slim"],
    queryFn: async () => {
      const res = await api.getAccounts();
      return (res as any[]).map((a: any) => ({ id: a.id, name: a.account_name || a.name || "" })).sort((a: any, b: any) => a.name.localeCompare(b.name));
    },
  });
  // "My Mail" (Phase 2) owner picker — a non-shared mailbox can be assigned to exactly one staffer.
  const { data: staffUsers = [] } = useQuery({
    queryKey: ["users", "assignable"],
    queryFn: () => api.getUsers({ assignable: true }),
  });

  const inv = () => queryClient.invalidateQueries({ queryKey: ["email-accounts"] });
  const err = (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" });

  const addMut = useMutation({
    mutationFn: () => emailApi.addEmailAccount({ mailbox_address: newMailbox.trim(), display_name: newDisplayName.trim() || undefined, is_shared_mailbox: isShared, is_tammy_tasker: isTammy, account_id: newLinkedAccountId || null, user_id: !isShared ? (newOwnerId || null) : null }),
    onSuccess: () => { setNewMailbox(""); setNewDisplayName(""); setIsShared(false); setIsTammy(false); setNewLinkedAccountId(""); setNewOwnerId(""); inv(); toast({ title: "Mailbox added" }); },
    onError: err,
  });
  const linkMut = useMutation({ mutationFn: ({ id, accountId }: { id: string; accountId: string | null }) => emailApi.linkEmailAccount(id, accountId), onSuccess: inv, onError: err });
  const toggleMut = useMutation({ mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => emailApi.toggleEmailAccount(id, isActive), onSuccess: inv, onError: err });
  const deleteMut = useMutation({ mutationFn: (id: string) => emailApi.deleteEmailAccount(id), onSuccess: () => { inv(); toast({ title: "Mailbox removed" }); }, onError: err });
  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof emailApi.updateEmailAccount>[1] }) => emailApi.updateEmailAccount(id, data),
    onSuccess: () => { setEditingId(null); inv(); toast({ title: "Mailbox updated" }); },
    onError: err,
  });

  function startEdit(a: EmailAccount) { setEditingId(a.id); setEditMailbox(a.mailbox_address); setEditDisplayName(a.display_name ?? ""); setEditIsShared(a.is_shared_mailbox); setEditIsTammy(a.is_tammy_tasker); setEditLinkedAccountId(a.account_id ?? ""); setEditOwnerId(a.user_id ?? ""); }
  function saveEdit() {
    if (!editingId) return;
    updateMut.mutate({ id: editingId, data: { mailbox_address: editMailbox.trim(), display_name: editDisplayName.trim() || undefined, is_shared_mailbox: editIsShared, is_tammy_tasker: editIsTammy, account_id: editIsShared ? (editLinkedAccountId || null) : null, user_id: !editIsShared ? (editOwnerId || null) : null } });
  }

  return (
    <Section icon={<Inbox className="w-5 h-5 text-primary" />} title="Microsoft 365 Email Accounts" description="Configure which M365 mailboxes Central Command searches for client emails. Requires Azure app registration with Mail.Read permissions.">
      {/* Add form */}
      <div className="rounded-md border p-4 space-y-3 mb-4">
        <p className="text-sm font-medium">Add Mailbox</p>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs mb-1 block">Email address *</Label><Input value={newMailbox} onChange={e => setNewMailbox(e.target.value)} placeholder="name@carpentercpas.com" className="h-8 text-sm" /></div>
          <div><Label className="text-xs mb-1 block">Display name</Label><Input value={newDisplayName} onChange={e => setNewDisplayName(e.target.value)} placeholder="e.g. Tammy Tasker" className="h-8 text-sm" /></div>
        </div>
        {isShared && (
          <div>
            <Label className="text-xs mb-1 block">Link to client account</Label>
            <select className="w-full h-8 text-sm border rounded px-2 bg-background" value={newLinkedAccountId} onChange={e => setNewLinkedAccountId(e.target.value)}>
              <option value="">— Not linked —</option>
              {allAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
        )}
        {!isShared && (
          <div>
            <Label className="text-xs mb-1 block">Owner ("My Mail" — leave blank for an unassigned mailbox)</Label>
            <select className="w-full h-8 text-sm border rounded px-2 bg-background" value={newOwnerId} onChange={e => setNewOwnerId(e.target.value)}>
              <option value="">— No owner —</option>
              {staffUsers.map((u: any) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        )}
        <div className="flex items-center gap-4 text-sm">
          <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={isShared} onChange={e => { setIsShared(e.target.checked); if (e.target.checked) setNewOwnerId(""); }} className="rounded" /> Shared mailbox</label>
          <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={isTammy} onChange={e => setIsTammy(e.target.checked)} className="rounded" /> Tammy Tasker</label>
        </div>
        <Button size="sm" className="h-8 gap-1" disabled={!newMailbox.trim() || addMut.isPending} onClick={() => addMut.mutate()}>
          <Plus className="w-3.5 h-3.5" /> Add Mailbox
        </Button>
      </div>

      {/* List */}
      {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : emailAccounts.length === 0 ? <p className="text-sm text-muted-foreground">No mailboxes configured.</p> : (
        <div className="border rounded-md divide-y">
          {[...emailAccounts].sort((a, b) => (a.display_name || a.mailbox_address).toLowerCase().localeCompare((b.display_name || b.mailbox_address).toLowerCase())).map(acct => (
            <div key={acct.id} className="px-4">
              {editingId === acct.id ? (
                <div className="py-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs mb-1 block">Email *</Label><Input value={editMailbox} onChange={e => setEditMailbox(e.target.value)} className="h-8 text-sm" /></div>
                    <div><Label className="text-xs mb-1 block">Display name</Label><Input value={editDisplayName} onChange={e => setEditDisplayName(e.target.value)} className="h-8 text-sm" /></div>
                  </div>
                  {editIsShared && (
                    <div><Label className="text-xs mb-1 block">Link to client</Label>
                      <select className="w-full h-8 text-sm border rounded px-2 bg-background" value={editLinkedAccountId} onChange={e => setEditLinkedAccountId(e.target.value)}>
                        <option value="">— Not linked —</option>
                        {allAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    </div>
                  )}
                  {!editIsShared && (
                    <div><Label className="text-xs mb-1 block">Owner ("My Mail")</Label>
                      <select className="w-full h-8 text-sm border rounded px-2 bg-background" value={editOwnerId} onChange={e => setEditOwnerId(e.target.value)}>
                        <option value="">— No owner —</option>
                        {staffUsers.map((u: any) => <option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="flex items-center gap-4 text-sm">
                    <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={editIsShared} onChange={e => { setEditIsShared(e.target.checked); if (e.target.checked) setEditOwnerId(""); }} className="rounded" /> Shared</label>
                    <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={editIsTammy} onChange={e => setEditIsTammy(e.target.checked)} className="rounded" /> Tammy</label>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="h-7 gap-1" disabled={!editMailbox.trim() || updateMut.isPending} onClick={saveEdit}><Check className="w-3 h-3" /> Save</Button>
                    <Button size="sm" variant="ghost" className="h-7 gap-1" onClick={() => setEditingId(null)}><X className="w-3 h-3" /> Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2 py-3">
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${!acct.is_active ? "line-through text-muted-foreground" : ""}`}>
                      {acct.display_name ? `${acct.display_name} (${acct.mailbox_address})` : acct.mailbox_address}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {acct.is_shared_mailbox && <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">shared</span>}
                      {acct.is_tammy_tasker && <span className="text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Tammy</span>}
                      {!acct.is_shared_mailbox && acct.owner_name && (
                        <span className="text-xs bg-violet-100 text-violet-800 px-1.5 py-0.5 rounded" title="My Mail owner">My Mail: {acct.owner_name}</span>
                      )}
                      {!acct.is_active && <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">inactive</span>}
                      {acct.is_shared_mailbox && (acct.account_id ? (
                        <span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded flex items-center gap-1">
                          → {allAccounts.find(a => a.id === acct.account_id)?.name ?? "Linked"}
                          <button className="hover:text-blue-600 ml-0.5" onClick={() => linkMut.mutate({ id: acct.id, accountId: null })}>×</button>
                        </span>
                      ) : (
                        <select className="text-xs border rounded px-1 py-0.5 bg-background h-5 max-w-[160px]" value="" onChange={e => { if (e.target.value) linkMut.mutate({ id: acct.id, accountId: e.target.value }); }} title="Link to client">
                          <option value="">Link to client…</option>
                          {allAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                        </select>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(acct)}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => toggleMut.mutate({ id: acct.id, isActive: !acct.is_active })} disabled={toggleMut.isPending}>
                      <Power className={`w-3.5 h-3.5 ${acct.is_active ? "text-green-600" : "text-muted-foreground"}`} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => deleteMut.mutate(acct.id)} disabled={deleteMut.isPending}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

// ─── SmartVault ───────────────────────────────────────────────────────────────

function SmartVaultSectionFull() {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const { data: status, isLoading, refetch } = useQuery<SVStatus>({
    queryKey: ["smartvault-status"],
    queryFn: getSmartVaultStatus,
    staleTime: 30_000,
  });

  async function handleTest() {
    setTesting(true); setTestResult(null);
    try {
      const r = await testSmartVaultConnection();
      setTestResult(r);
      if (r.success) refetch();
    } catch (e: any) { setTestResult({ success: false, message: e?.message ?? "Failed" }); }
    finally { setTesting(false); }
  }

  const cfg = status?.configured;
  const allConfigured = cfg && cfg.clientId && cfg.privateKey && cfg.adminEmail && cfg.accountId;
  const envVars: { key: keyof SVConfigured; label: string; envName: string }[] = [
    { key: "clientId", label: "Client ID", envName: "SV_CLIENT_ID" },
    { key: "privateKey", label: "Private Key", envName: "SV_PRIVATE_KEY" },
    { key: "adminEmail", label: "Admin Email", envName: "SV_ADMIN_EMAIL" },
    { key: "accountId", label: "Account ID", envName: "SV_ACCOUNT_ID" },
  ];

  const badge = isLoading
    ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
    : status?.connected
    ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-xs">Connected</Badge>
    : allConfigured
    ? <Badge className="bg-red-100 text-red-800 border-red-200 text-xs">Error</Badge>
    : <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs">Not Configured</Badge>;

  const [open, setOpen] = useState(false);
  return (
    <Card>
      <CardHeader className="cursor-pointer select-none py-4" onClick={() => setOpen(v => !v)}>
        <CardTitle className="flex items-center gap-2">
          <HardDrive className="w-5 h-5 text-primary" />
          <span className="flex-1">SmartVault Integration</span>
          {badge}
          {open ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
        </CardTitle>
        {open && <CardDescription className="mt-1.5">Provision client vaults and route portal uploads to SmartVault. Requires a SmartVault Developer account and RSA key pair.</CardDescription>}
      </CardHeader>
      {open && (
        <CardContent className="space-y-5">
          <div className="rounded-md border p-4 space-y-2">
            <p className="text-sm font-medium mb-3">Environment Variables</p>
            {envVars.map(({ key, label, envName }) => {
              const isSet = cfg?.[key] ?? false;
              return (
                <div key={key} className="flex items-center gap-2 text-sm">
                  {isSet ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />}
                  <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">{envName}</code>
                  <span className="text-muted-foreground">— {label}</span>
                  {isSet ? <span className="text-xs text-emerald-600 ml-auto">Set</span> : <span className="text-xs text-amber-600 ml-auto">Missing</span>}
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-3">
            <Button size="sm" variant="outline" onClick={handleTest} disabled={testing || !allConfigured} className="gap-2">
              {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <HardDrive className="w-3.5 h-3.5" />}
              Test Connection
            </Button>
            {!allConfigured && <span className="text-xs text-muted-foreground">Set all 4 env vars first</span>}
          </div>
          {testResult && (
            <div className={`rounded-md border p-3 text-sm flex items-start gap-2 ${testResult.success ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"}`}>
              {testResult.success ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
              {testResult.message}
            </div>
          )}
          {status?.connected && (
            <div className="text-xs text-muted-foreground space-y-0.5">
              <p>Account ID: <code className="font-mono">{status.accountId}</code></p>
              <p>Admin email: <code className="font-mono">{status.adminEmail}</code></p>
            </div>
          )}
          <details className="text-sm">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground select-none">Setup instructions</summary>
            <div className="mt-3 space-y-3 text-muted-foreground">
              <p>1. Generate an RSA key pair:</p>
              <pre className="bg-muted rounded p-2 text-xs font-mono whitespace-pre-wrap">{`openssl genrsa -out sv_private.pem 2048\nopenssl rsa -in sv_private.pem -pubout -outform PEM -out sv_public.pem`}</pre>
              <p>2. Register at <strong>developer.smartvault.com</strong> and upload your public key.</p>
              <p>3. Add to server <code className="font-mono text-xs bg-muted px-1 rounded">.env</code> and restart.</p>
            </div>
          </details>
        </CardContent>
      )}
    </Card>
  );
}

// ─── Twilio SMS ───────────────────────────────────────────────────────────────

type TwilioStatus = {
  configured: boolean;
  vars: { accountSid: boolean; authToken: boolean; fromNumber: boolean };
  fromNumber: string | null;
};

function TwilioSMSSection() {
  const { toast } = useToast();
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [confirmMode, setConfirmMode] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState<{ sid: string } | null>(null);

  const { data: status, isLoading } = useQuery<TwilioStatus>({
    queryKey: ["twilio-status"],
    queryFn: () => fetchWithAuth<TwilioStatus>("/api/admin/sms/status"),
    staleTime: 60_000,
  });

  const charCount = message.length;
  const segments = charCount === 0 ? 0 : Math.ceil(charCount / 160);

  const envVars = [
    { key: "accountSid" as const, label: "Account SID", envName: "TWILIO_ACCOUNT_SID" },
    { key: "authToken" as const, label: "Auth Token", envName: "TWILIO_AUTH_TOKEN" },
    { key: "fromNumber" as const, label: "From Number", envName: "TWILIO_FROM_NUMBER" },
  ];

  async function handleSend() {
    setSending(true);
    setLastResult(null);
    try {
      const r = await fetchWithAuth<{ sent: boolean; sid: string }>("/api/admin/sms/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: to.trim(), body: message.trim() }),
      });
      setLastResult({ sid: r.sid });
      toast({ title: "SMS sent", description: `To ${to} — SID: ${r.sid.slice(0, 16)}…` });
      setTo("");
      setMessage("");
      setConfirmMode(false);
    } catch (e: any) {
      toast({ title: "Send failed", description: e.message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  }

  const badge = isLoading
    ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
    : status?.configured
    ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-xs">Configured</Badge>
    : <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs">Not Configured</Badge>;

  const [open, setOpen] = useState(false);
  return (
    <Card>
      <CardHeader className="cursor-pointer select-none py-4" onClick={() => setOpen(v => !v)}>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-primary" />
          <span className="flex-1">Twilio SMS</span>
          {badge}
          {open ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
        </CardTitle>
        {open && <CardDescription className="mt-1.5">Send SMS messages to clients from the firm's Twilio number. Admin only.</CardDescription>}
      </CardHeader>
      {open && (
        <CardContent className="space-y-5">
          {/* Env var status */}
          <div className="rounded-md border p-4 space-y-2">
            <p className="text-sm font-medium mb-3">Environment Variables</p>
            {envVars.map(({ key, label, envName }) => {
              const isSet = status?.vars?.[key] ?? false;
              return (
                <div key={key} className="flex items-center gap-2 text-sm">
                  {isSet ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />}
                  <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">{envName}</code>
                  <span className="text-muted-foreground">— {label}</span>
                  {isSet ? <span className="text-xs text-emerald-600 ml-auto">Set</span> : <span className="text-xs text-amber-600 ml-auto">Missing</span>}
                </div>
              );
            })}
            {status?.fromNumber && (
              <p className="text-xs text-muted-foreground mt-2">Sending from: <code className="font-mono">{status.fromNumber}</code></p>
            )}
          </div>

          {/* Compose form */}
          {status?.configured ? (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>To (phone number)</Label>
                <Input
                  value={to}
                  onChange={e => { setTo(e.target.value); setConfirmMode(false); setLastResult(null); }}
                  placeholder="+19195551234"
                  className="font-mono"
                  disabled={sending}
                />
                <p className="text-xs text-muted-foreground">E.164 format required (e.g. +19195551234)</p>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>Message</Label>
                  <span className={`text-xs ${charCount > 160 ? "text-amber-600" : "text-muted-foreground"}`}>
                    {charCount} chars · {segments} segment{segments !== 1 ? "s" : ""}
                  </span>
                </div>
                <textarea
                  value={message}
                  onChange={e => { setMessage(e.target.value); setConfirmMode(false); setLastResult(null); }}
                  placeholder="Type your message…"
                  rows={4}
                  maxLength={1600}
                  disabled={sending}
                  className="w-full border rounded-md px-3 py-2 text-sm bg-background resize-y min-h-[80px] max-h-[300px] focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                />
              </div>

              {!confirmMode ? (
                <Button
                  size="sm"
                  className="gap-2"
                  disabled={!to.trim() || !message.trim() || sending}
                  onClick={() => setConfirmMode(true)}
                >
                  <Send className="w-4 h-4" /> Review & Send
                </Button>
              ) : (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-4 space-y-3">
                  <p className="text-sm font-medium text-amber-900">Confirm — this sends a real SMS</p>
                  <div className="text-sm text-amber-800 space-y-1">
                    <p><span className="font-medium">To:</span> {to}</p>
                    <p><span className="font-medium">From:</span> {status.fromNumber}</p>
                    <p className="font-medium">Message:</p>
                    <p className="bg-white border rounded p-2 whitespace-pre-wrap text-foreground">{message}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="gap-2" disabled={sending} onClick={handleSend}>
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      {sending ? "Sending…" : "Confirm Send"}
                    </Button>
                    <Button size="sm" variant="ghost" disabled={sending} onClick={() => setConfirmMode(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {lastResult && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-2 text-sm text-emerald-800">
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  Sent — SID: <code className="font-mono text-xs">{lastResult.sid}</code>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Set all three environment variables above to enable SMS sending.</p>
          )}
        </CardContent>
      )}
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AdminConfig() {
  const { user } = useAuth();
  const [, navigate] = useLocation();

  if (user && user.role !== "admin") {
    navigate("/settings");
    return null;
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-4">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight mb-2">Admin Settings</h1>
          <p className="text-muted-foreground">System-level configuration for administrators.</p>
        </div>

        <InviteTeamSection />

        <LinkSection
          icon={<Users2 className="w-5 h-5 text-primary" />}
          title="Users & Roles"
          description="View all team members, change roles, deactivate accounts, and manage access levels."
          href="/users"
          buttonLabel="Manage Users"
        />

        <ActivityCodesSection />

        <LinkSection
          icon={<CalendarDays className="w-5 h-5 text-primary" />}
          title="Pay Periods"
          description="View and manage pay periods, close periods, and handle time entry approvals."
          href="/periods"
          buttonLabel="Manage Pay Periods"
        />

        <LinkSection
          icon={<Upload className="w-5 h-5 text-primary" />}
          title="Data Import"
          description="Import client data, projects, and historical records from Financial Cents or other sources."
          href="/admin/import"
          buttonLabel="Open Data Import"
        />

        <LinkSection
          icon={<Zap className="w-5 h-5 text-amber-500" />}
          title="Automation Rules"
          description="Manage task-completion automators: add/remove tags, send emails, and create projects from templates."
          href="/settings/automation-rules"
          buttonLabel="Manage Automations"
        />

        <LinkSection
          icon={<FileSignature className="w-5 h-5 text-violet-600" />}
          title="Agreement Templates"
          description="Create and manage agreement templates for client engagements, fixed price agreements, and other documents sent for e-signature via SignWell."
          href="/admin/agreements"
          buttonLabel="Manage Templates"
        />

        <EmailConfigSection />
        <DailyTimeReportSection />
        <M365Section />
        <TwilioSMSSection />
        <SmartVaultSectionFull />
      </div>
    </Layout>
  );
}
